import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { useAuth } from "./context/AuthContext";
import ScrollToTop from './components/ScrollToTop'

// Route page components are lazy-loaded so each route ships its own chunk.
// This keeps the initial payload small (a blog reader no longer downloads
// admin/recharts, search/airport-data, the 3D airplane, etc.).
const Index = lazy(() => import("./pages/Index"));
const Search = lazy(() => import("./pages/Search"));
const Results = lazy(() => import("./pages/Results"));
const CompareFlights = lazy(() => import("./pages/CompareFlights"));
const FlightDetail = lazy(() => import("./pages/FlightDetail"));
const AwardBookingGuide = lazy(() => import("./pages/AwardBookingGuide"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostDetail = lazy(() => import("./pages/BlogPostDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQs = lazy(() => import("./pages/FAQs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const WebsiteDisclaimer = lazy(() => import("./pages/WebsiteDisclaimer"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/SuperAdmin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const SavedFlights = lazy(() => import("./pages/SavedFlights"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

import Navbar from "./components/layout/Navbar";
import MobileBottomBar from "./components/layout/MobileBottomBar";
import { RequireAuth } from "./components/auth/RequireAuth";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

const queryClient = new QueryClient();

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isLoggedIn) return <Navigate to="/auth?mode=signin&redirect=/admin" replace />;
  const role = (user?.role ?? '').toLowerCase();
  if (role !== 'admin' && role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <ThemeProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <Navbar />
          <div className="lg:pb-0 pb-16">
          <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/search" element={<Search />} />
            <Route path="/results" element={<Results />} />
            <Route path="/compare" element={<RequireAuth title="Sign in to compare flights"><CompareFlights /></RequireAuth>} />
            <Route path="/flight-review" element={<FlightDetail />} />
            <Route path="/booking-guide" element={<AwardBookingGuide />} />
            <Route path="/flight/:id" element={<FlightDetail />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/disclaimer" element={<WebsiteDisclaimer />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/" element={<Auth />} />
            <Route path="/dashboard" element={<RequireAuth title="Sign in to view your dashboard"><Dashboard /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth title="Sign in to manage your account"><Settings /></RequireAuth>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/saved-flights" element={<RequireAuth title="Sign in to view your saved flights"><SavedFlights /></RequireAuth>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </div>
          <MobileBottomBar />
        </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
